import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Compass, FolderHeart, Route } from "lucide-react";
import api from "@/lib/api";
import { useApp } from "@/context/AppContext";
import PlaceCard from "@/components/PlaceCard";
import ShareButton from "@/components/ShareButton";
import { useToast } from "@/hooks/use-toast";

export default function SharedList() {
  const { shareId } = useParams();
  const navigate = useNavigate();
  const { t, lists, createList, setLists } = useApp();
  const { toast } = useToast();
  const [share, setShare] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/shares/${shareId}`).then((r) => setShare(r.data)).catch(() => setShare(null)).finally(() => setLoading(false));
  }, [shareId]);

  const saveToLists = () => {
    const id = createList(share.title);
    setLists((prev) => prev.map((l) => (l.id === id ? { ...l, placeIds: share.place_ids } : l)));
    toast({ title: t("saved_to_lists") });
  };

  if (loading) return <div className="py-20 text-center text-ink-muted">{t("loading")}</div>;
  if (!share) return (
    <div className="flex flex-col items-center gap-3 py-20 text-center" data-testid="share-not-found">
      <Route className="h-10 w-10 text-ink-muted" />
      <p className="font-heading font-bold text-ink">{t("share_not_found")}</p>
      <button onClick={() => navigate("/")} className="rounded-full bg-terracotta px-4 py-2 text-sm font-bold text-white">{t("open_app")}</button>
    </div>
  );

  const alreadySaved = lists.some((l) => l.name === share.title && JSON.stringify(l.placeIds) === JSON.stringify(share.place_ids));
  const shareUrl = `${window.location.origin}/s/${share.id}`;

  return (
    <div className="space-y-5" data-testid="shared-list-page">
      <div className="-mx-4 -mt-4 bg-sea px-5 pb-6 pt-8 text-white">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-white/15 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide"><Route className="h-3 w-3" /> {t("shared_list")}</span>
            <h1 data-testid="shared-list-title" className="mt-3 break-words font-heading text-2xl font-extrabold leading-tight [overflow-wrap:anywhere]">{share.title}</h1>
            <p className="mt-1 text-sm opacity-80">{share.places.length} {t("places_in_collection")}</p>
          </div>
          <ShareButton title={share.title} url={shareUrl} light testid="shared-reshare-btn" />
        </div>
      </div>

      <div className="flex flex-col gap-2 sm:flex-row">
        <button data-testid="shared-save-btn" onClick={saveToLists} disabled={alreadySaved} className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-terracotta py-3 text-sm font-bold text-white disabled:opacity-60">
          <FolderHeart className="h-4.5 w-4.5" /> {alreadySaved ? t("saved_to_lists") : t("save_to_lists")}
        </button>
        <button data-testid="shared-open-app" onClick={() => navigate("/")} className="flex items-center justify-center gap-2 rounded-xl border border-border bg-white px-4 py-3 text-sm font-bold text-ink">
          <Compass className="h-4.5 w-4.5 text-sea" /> {t("open_app")}
        </button>
      </div>

      <div className="grid grid-cols-2 gap-4">
        {share.places.map((p) => <PlaceCard key={p.id} place={p} />)}
      </div>
    </div>
  );
}
